import type { DeviceChallengeDto, DeviceProofDto, DeviceProofPurpose } from '@fas/types';
import { api } from './api';
import { useAuthStore } from './auth-store';
import { base64url, loadDeviceKey } from './device';

/*
 * Device proofs (Phase 2B, ADR-043): the device key signs the purpose, a short-lived challenge
 * from the server and the request id. Challenges belong to one signed-in user and are reused
 * for a few minutes, so a clock request rarely waits for one.
 */

/** Reuse a challenge this long after receiving it (the server accepts it for 5 minutes). */
const REUSE_MS = 4 * 60_000;

let cached: { userId: string; challenge: string; until: number } | null = null;
let inflight: Promise<string> | null = null;

function currentUserId(): string {
  return useAuthStore.getState().me?.user.id ?? '';
}

async function challenge(): Promise<string> {
  const userId = currentUserId();
  if (cached && cached.userId === userId && cached.until > Date.now()) return cached.challenge;
  inflight ??= api
    .post<DeviceChallengeDto>('/devices/challenge')
    .then((c) => {
      // Measured from receipt, so a wrong device clock does not matter.
      cached = { userId, challenge: c.challenge, until: Date.now() + REUSE_MS };
      return c.challenge;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

/** Fetches a challenge ahead of a clock action (e.g. while the GPS fix is taken). */
export function prefetchDeviceChallenge(): void {
  void loadDeviceKey()
    .then((key) => (key ? challenge() : null))
    .catch(() => undefined);
}

/** The device's proof for a request, or null where this browser cannot hold a device key. */
export async function deviceProof(purpose: DeviceProofPurpose, requestId: string): Promise<DeviceProofDto | null> {
  const key = await loadDeviceKey();
  if (!key) return null;
  const c = await challenge();
  const message = `fas-device-proof/v1\n${purpose}\n${c}\n${requestId}`;
  const signature = await crypto.subtle.sign({ name: 'ECDSA', hash: 'SHA-256' }, key.privateKey, new TextEncoder().encode(message));
  return { publicKey: key.publicKey, challenge: c, signature: base64url(new Uint8Array(signature)) };
}

/**
 * Signature for a clock event recorded offline (Phase 2D): no challenge can be fetched, so the
 * device's capture time is signed with the request id. Needs no connection.
 */
export async function offlineDeviceProof(
  purpose: 'OFFLINE_CLOCK_IN' | 'OFFLINE_CLOCK_OUT',
  capturedAt: string,
  requestId: string,
): Promise<{ publicKey: DeviceProofDto['publicKey']; signature: string } | null> {
  const key = await loadDeviceKey();
  if (!key) return null;
  const message = `fas-device-proof/v1\n${purpose}\n${capturedAt}\n${requestId}`;
  const signature = await crypto.subtle.sign({ name: 'ECDSA', hash: 'SHA-256' }, key.privateKey, new TextEncoder().encode(message));
  return { publicKey: key.publicKey, signature: base64url(new Uint8Array(signature)) };
}

/** Forget the cached challenge (after signing in or out). */
export function resetDeviceChallenge(): void {
  cached = null;
}
